import { extractRouteFromPdf } from '../services/pdf-parser.js';
import { resolveMissingPoints } from '../services/point-resolver.js';
import { state, setState, updateCurrentRoute, createEmptyRoute } from '../lib/state.js';
import { saveRouteSnapshot, loadRouteSnapshot } from '../services/repository.js';
import { drawRoute, captureMap } from '../ui/map.js';
import { els } from '../app/dom.js';
import { requestRender } from '../app/events.js';
import { showWorkspace } from '../app/navigation.js';
import { reloadRoutes } from './route-data.js';
import { requireAuthenticatedSession } from '../app/auth.js';
import { syncMotorwaySectionSettingsToLine } from '../calculators/motorways.js';
import { alertMytoCzPointLimit, mytoCzPointLimitExceeded } from '../calculators/myto-cz.js';
import { setStatus, capitalize, deriveLineNumber, inferCourseType, routeCountriesArray, hasCoords } from '../lib/app-helpers.js';
import { actualRouteStops, getRoutePlanPoints, isRouteConstraint, rebuildRouteFromPlan, routeCountriesLabel, missingRequestedBorderPairs } from './route-plan.js';
import { syncEditableRowsToRoutePlan } from './route-editor.js';

export function newRoute(){ setState({currentRoute:createEmptyRoute()}); els.pdfFile.value=''; requestRender(); }

export async function importPdf() {
  const file=els.pdfFile.files?.[0]; if(!file)return;
  setStatus(els.importStatus,'Odczytuję wszystkie przystanki i aktywne linki GPS z PDF…','working');
  try {
    const parsed=await extractRouteFromPdf(await file.arrayBuffer(),file.name);
    const importedPoints=parsed.points.map((p,i)=>({...p,id:p.id||crypto.randomUUID(),position:i+1,pointType:'stop',isBorder:false}));

    const points=await resolveMissingPoints(
      importedPoints,
      msg=>setStatus(els.importStatus,msg,'working')
    );

    const importedDirection=capitalize(parsed.direction);
    const importedLineNumber=deriveLineNumber(parsed.routeTitle,file.name);
    const importedDirectionType=inferCourseType(`${parsed.direction || ''} ${file.name || ''}`);
    setState({currentRoute:{
      ...createEmptyRoute(),
      title:parsed.routeTitle,
      lineNumber:importedLineNumber,
      direction:importedDirection,
      directionType:importedDirectionType,
      carrier:'',
      sourceFileName:file.name,
      sourceFile:file,
      originalPoints:points,
      dirty:true,
    }});
    els.routeTitle.value=parsed.routeTitle;
    els.routeLineNumber.value=importedLineNumber;
    els.routeDirection.value=importedDirection;
    els.routeDirectionType.value=importedDirectionType;
    els.routeCarrier.value='';
    const missing=points.filter(p=>!hasCoords(p));
    const linkCount=points.filter(p=>p.mapsUrl).length;
    const message=`Odczytano ${points.length} przystanków z ${parsed.pagesCount} stron. Aktywne linki GPS: ${linkCount}.`;
    setStatus(
      els.importStatus,
      missing.length ? `${message} Nie rozwiązano ${missing.length}: ${missing.map(p=>p.name).join(', ')}.` : `${message} Wszystkie punkty mają GPS.`,
      missing.length?'warning':'ok'
    );
    requestRender();
  } catch(error){ setStatus(els.importStatus,`Nie udało się odczytać PDF:
${error.message}`,'error'); }
}
export async function resolvePoints(){
  setStatus(els.importStatus,'Uzupełniam brakujące GPS…','working');
  try { const points=await resolveMissingPoints(actualRouteStops(state.currentRoute),msg=>setStatus(els.importStatus,msg,'working')); updateCurrentRoute({originalPoints:points}); const missing=points.filter(p=>!hasCoords(p)); setStatus(els.importStatus,missing.length?`Nie rozwiązano ${missing.length} punktów. Popraw je ręcznie.`:'Wszystkie punkty mają GPS.','ok'); requestRender(); }
  catch(error){setStatus(els.importStatus,error.message,'error');}
}
export async function analyzeRoute(){
  syncHeaderFields();
  // Odczytujemy wartości bezpośrednio z pól tabeli. Dzięki temu kliknięcie
  // „Wyznacz trasę” nie może zgubić nazwy aktualnie edytowanego punktu i
  // zastąpić jej wcześniejszym „Nowy punkt”.
  syncEditableRowsToRoutePlan();
  const initialPlan = getRoutePlanPoints(state.currentRoute);
  setStatus(els.importStatus,'Sprawdzam i uzupełniam brakujące GPS…','working');

  try {
    const resolvedPlan = await resolveMissingPoints(
      initialPlan,
      msg => setStatus(els.importStatus,msg,'working')
    );

    const missing = resolvedPlan.filter(point => !hasCoords(point));
    if (missing.length) {
      const details = missing
        .map(point => `${point.name}${point.resolveError ? ` (${point.resolveError})` : ''}`)
        .join(', ');
      throw new Error(`Nie udało się ustalić GPS dla: ${details}`);
    }

    const constraintCount = resolvedPlan.filter(isRouteConstraint).length;
    setStatus(
      els.importStatus,
      constraintCount
        ? 'Wyznaczam od początku całą trasę przez edytowane przystanki i granice…'
        : 'Wyznaczam najszybszą trasę i punkty graniczne…',
      'working'
    );

    const analysis = await rebuildRouteFromPlan(
      resolvedPlan,
      msg => setStatus(els.importStatus,msg,'working')
    );

    updateCurrentRoute({
      originalPoints:analysis.originalPoints,
      routeConstraints:analysis.routeConstraints,
      routePlanPoints:analysis.routePlanPoints,
      routeEditPreview:null,
      pointEditMode:false,
      expandedPoints:analysis.expandedPoints,
      routeGeometry:analysis.geometry,
      routeLegs:analysis.legs,
      sections:analysis.sections,
      distanceMeters:analysis.distanceMeters,
      durationSeconds:analysis.durationSeconds,
      mapScreenshotDataUrl:'',
      results:{}
    });

    const mytoLimitViolations = analysis.sections.filter(mytoCzPointLimitExceeded);
    if (mytoLimitViolations.length) {
      const section = mytoLimitViolations[0];
      const message = alertMytoCzPointLimit(section);
      setStatus(els.importStatus, message, 'warning');
    }

    drawRoute(state.currentRoute);
    updateCurrentRoute({mapScreenshotDataUrl:captureMap(),dirty:true});
    const countries = routeCountriesLabel(analysis.sections);
    const missingPairs = missingRequestedBorderPairs(analysis.routePlanPoints, analysis.expandedPoints);
    setStatus(
      els.importStatus,
      missingPairs.length
        ? `Trasa została przeliczona, ale GPS punktów nie wymusił wymaganych granic: ${missingPairs.join(', ')}. Edytuj te wiersze, ustaw rzeczywiste współrzędne przejść i przelicz ponownie. Aktualny przebieg: ${countries || 'nieustalony'}.`
        : `Gotowe: ${analysis.sections.length} odcinków państwowych, ${analysis.expandedPoints.filter(p=>p.isBorder).length} punktów granicznych${countries ? `. Przebieg: ${countries}` : ''}. Możesz teraz uruchomić właściwe kalkulatory.`,
      missingPairs.length ? 'warning' : 'ok'
    );
    requestRender();
  } catch(error){
    setStatus(els.importStatus,`Błąd wyznaczania trasy:
${error.message}`,'error');
  }
}

export async function saveRoute(){
  syncHeaderFields();
  if(!state.currentRoute.sections.length)return setStatus(els.importStatus,'Najpierw wyznacz trasę i granice.','error');
  if(!state.currentRoute.lineNumber)return setStatus(els.importStatus,'Podaj numer linii.','error');
  if(!state.currentRoute.carrier)return setStatus(els.importStatus,'Wybierz przewoźnika INTER albo UNIBUS.','error');
  if (!(await requireAuthenticatedSession('zapisać linię w Supabase'))) return;
  setStatus(els.importStatus,'Zapisuję pełną linię, kraje, opłaty, zrzuty i aktualną wersję w Supabase…','working');
  try {
    const route={
      ...state.currentRoute,
      directionType:state.currentRoute.directionType || 'outbound',
      mapScreenshotDataUrl:captureMap()||state.currentRoute.mapScreenshotDataUrl,
    };
    const saved=await saveRouteSnapshot(state.organizationId,route);
    await syncMotorwaySectionSettingsToLine(route);
    updateCurrentRoute({
      id:saved.route_id,
      versionId:saved.version_id,
      countries:routeCountriesArray(route.sections),
      dirty:false,
    });
    await reloadRoutes();
    const calculated=Object.keys(route.results||{}).length;
    const dayCount=new Date(new Date().getFullYear(),new Date().getMonth()+1,0).getDate();
    setStatus(els.importStatus,`Linia ${route.lineNumber} została zapisana jednocześnie jako wyjazd i powrót wraz z ${route.sections.length} odcinkami i ${calculated} wynikami opłat. Kalendarz bieżącego miesiąca: ${dayCount} wyjazdów + ${dayCount} powrotów.`,'ok');
    requestRender();
  }
  catch(error){setStatus(els.importStatus,`Błąd zapisu: ${error.message}`,'error');}
}


export async function loadSavedRoute(record){try{showWorkspace('route');setStatus(els.importStatus,'Wczytuję zapisaną linię…','working');const snapshot=await loadRouteSnapshot(record.id);if(!snapshot)throw new Error('Brak zapisanej wersji linii.');setState({currentRoute:snapshot});setStatus(els.importStatus,'Wczytano zapisaną linię.','ok');requestRender();}catch(error){setStatus(els.importStatus,`Nie udało się wczytać linii: ${error.message}`,'error');}}

export function syncHeaderFields(write=true){
  if(write){
    updateCurrentRoute({
      title:els.routeTitle.value.trim(),
      lineNumber:els.routeLineNumber?.value.trim()||deriveLineNumber(els.routeTitle.value),
      direction:els.routeDirection.value.trim(),
      carrier:String(els.routeCarrier?.value||'').toUpperCase(),
      directionType:els.routeDirectionType?.value||state.currentRoute.directionType||'outbound',
      dirty:true,
    });
  }else{
    els.routeTitle.value=state.currentRoute.title||'';
    if(els.routeLineNumber)els.routeLineNumber.value=state.currentRoute.lineNumber||deriveLineNumber(state.currentRoute.title||state.currentRoute.sourceFileName||'');
    els.routeDirection.value=state.currentRoute.direction||'';
    if(els.routeCarrier)els.routeCarrier.value=state.currentRoute.carrier||'';
    if(els.routeDirectionType)els.routeDirectionType.value=state.currentRoute.directionType||'outbound';
  }
}


